import { useEffect, useRef } from 'react';

interface UseUserActivityOptions {
  /** Minimum interval between callback fires. Default 10s. */
  throttleMs?: number;
  /** Disable listeners entirely when false. Default true. */
  enabled?: boolean;
}

/**
 * Fires `onActivity` when any of the following signals indicate the user
 * is present / active again: tab visibility returns to `visible`, network
 * goes `online`, window focuses, key is pressed, page scrolls, touch
 * starts, click. Debounced — once `onActivity` fires, further events are
 * ignored for `throttleMs`.
 *
 * Listeners attach only when `enabled` is true. Toggle `enabled` from your
 * own state (e.g. "channel is currently disconnected") so a healthy app
 * pays zero per-event cost.
 *
 * `mousemove` is intentionally NOT listened for — it fires every animation
 * frame the cursor is moving and the cost adds up across the whole app,
 * even with throttling inside the handler. `click` covers the resting-mouse
 * case; `scroll` / `keydown` / `touchstart` cover everything else.
 *
 * Use it for cheap recovery actions (re-subscribe a stale realtime channel,
 * refetch a slow-changing query, restart a poll). The callback gates its
 * own work — this hook only tells you "the user just did something."
 *
 * The callback is read via a ref so a fresh inline lambda on each render
 * doesn't churn the event listeners. Listeners are passive where possible.
 */
export function useUserActivity(
  onActivity: () => void,
  options: UseUserActivityOptions = {},
): void {
  const { throttleMs = 10_000, enabled = true } = options;

  const cbRef = useRef(onActivity);
  cbRef.current = onActivity;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    let last = 0;

    const fire = () => {
      const now = Date.now();
      if (now - last < throttleMs) return;
      last = now;
      cbRef.current();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') fire();
    };

    window.addEventListener('online', fire);
    window.addEventListener('focus', fire);
    window.addEventListener('click', fire);
    window.addEventListener('keydown', fire);
    window.addEventListener('scroll', fire, { passive: true, capture: true });
    window.addEventListener('touchstart', fire, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('online', fire);
      window.removeEventListener('focus', fire);
      window.removeEventListener('click', fire);
      window.removeEventListener('keydown', fire);
      window.removeEventListener(
        'scroll',
        fire,
        { capture: true } as EventListenerOptions,
      );
      window.removeEventListener('touchstart', fire);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, throttleMs]);
}
